<template>
  <dialog
    ref="modal"
    @cancel="handleOnCancel"
    @close="handleOnToggle"
    @open="handleOnToggle"
    @toggle="handleOnToggle"
    :class="[
      'scheme-box hidden [&[open]]:flex py-6 max-w-[94vw]',
      draggable && dragging ? 'cursor-grabbing' : '',
    ]"
    :style="dialogStyle"
    @pointerdown="onDragPointerDown"
  >
    <div class="overflow-auto flex flex-col px-6 w-full">
      <slot></slot>
    </div>
    <Button v-if="!hideClose" @click="close" variant="ghost" icon class="absolute top-2 right-1">
      <Icon name="cross" size="sm" />
      <span class="sr-only">Close</span>
    </Button>
  </dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import Button from "@/components/theme/ui/Button.vue";
import Icon from "@/components/theme/ui/Icon.vue";

defineOptions({ name: "Modal" });

const props = withDefaults(
  defineProps<{ open?: boolean; draggable?: boolean; hideClose?: boolean }>(),
  {
    open: false,
    draggable: false,
    hideClose: false,
  },
);

const emit = defineEmits<{
  open: [];
  close: [];
  cancel: [];
  toggle: [open: boolean];
}>();

const modal = ref<HTMLDialogElement | null>(null);
const dragOffset = ref({ x: 0, y: 0 });
const dragging = ref(false);
const dragPointerId = ref<number | null>(null);
const dragStart = ref({ x: 0, y: 0, ox: 0, oy: 0 });

const dialogStyle = computed<Record<string, string>>(() => {
  if (!props.draggable) return {};
  return {
    transform: `translate(${dragOffset.value.x}px, ${dragOffset.value.y}px)`,
  };
});

function resetDragPosition() {
  dragOffset.value = { x: 0, y: 0 };
}

function teardownDragListeners() {
  window.removeEventListener("pointermove", onDragPointerMove);
  window.removeEventListener("pointerup", onDragPointerUp);
  window.removeEventListener("pointercancel", onDragPointerUp);
  dragging.value = false;
  dragPointerId.value = null;
}

function onDragPointerDown(e: PointerEvent) {
  if (!props.draggable) return;
  const el = e.target as HTMLElement | null;
  if (!el) return;
  if (el.closest("button, a, input, textarea, select, [data-modal-no-drag]")) return;
  if (!el.closest(".modal-drag-handle")) return;
  e.preventDefault();
  dragging.value = true;
  dragPointerId.value = e.pointerId;
  dragStart.value = {
    x: e.clientX,
    y: e.clientY,
    ox: dragOffset.value.x,
    oy: dragOffset.value.y,
  };
  window.addEventListener("pointermove", onDragPointerMove);
  window.addEventListener("pointerup", onDragPointerUp);
  window.addEventListener("pointercancel", onDragPointerUp);
}

function onDragPointerMove(e: PointerEvent) {
  if (!dragging.value || e.pointerId !== dragPointerId.value) return;
  dragOffset.value = {
    x: dragStart.value.ox + (e.clientX - dragStart.value.x),
    y: dragStart.value.oy + (e.clientY - dragStart.value.y),
  };
}

function onDragPointerUp(e: PointerEvent) {
  if (e.pointerId !== dragPointerId.value) return;
  teardownDragListeners();
}

function show() {
  if (!modal.value) return;
  if (props.draggable) resetDragPosition();
  modal.value.showModal();
  modal.value.focus();
  document.body.style.overflow = "hidden";
}

function close() {
  if (!modal.value) return;
  modal.value.close();
  document.body.style.overflow = "";
}

function handleOnToggle() {
  if (!modal.value) return;
  const isOpen = modal.value.open;
  emit(isOpen ? "open" : "close");
  emit("toggle", isOpen);
  document.body.style.overflow = isOpen ? "hidden" : "";
  if (!isOpen) {
    teardownDragListeners();
    if (props.draggable) resetDragPosition();
  }
}

function handleOnCancel(event: Event) {
  event.preventDefault();
  emit("cancel");
}

watch(
  () => props.open,
  (newVal) => {
    if (newVal) show();
    else close();
  },
);

onMounted(() => {
  if (props.open) show();
});

onBeforeUnmount(() => {
  teardownDragListeners();
  document.body.style.overflow = "";
});

defineExpose({ show, close });
</script>

<style scoped>
dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
